import { useEffect, useMemo, useState } from 'react';
import {
  riskAnalysis, pdf as pdfApi,
  type AnalizaMeta, type AnalizaInputData, type AnalizaCalcResult,
  type AnalizaMatrix, type AnalizaField, type AnalizaCalcRun,
} from '@/api/endpoints';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Icon } from '@/components/Icon';
import { PcvMatrix } from './PcvMatrix';
import { AnalizaExtractModal } from './AnalizaExtractModal';

type Tab = 'dane' | 'obliczenia' | 'macierz';
type Layer = { m?: number | string; ne?: number | string; k?: number | string };

/** Sekcja analizy ryzyka (art. 133) w szczegółach opracowania typu ANALIZA_RYZYKA_6. */
export function AnalizaRyzykaCard({ studyId, readOnly }: { studyId: number; readOnly?: boolean }) {
  const [meta, setMeta] = useState<AnalizaMeta | null>(null);
  const [input, setInput] = useState<AnalizaInputData | null>(null);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [calc, setCalc] = useState<AnalizaCalcResult[]>([]);
  const [calcRun, setCalcRun] = useState<AnalizaCalcRun | null>(null);
  const [matrix, setMatrix] = useState<AnalizaMatrix | null>(null);
  const [tab, setTab] = useState<Tab>('dane');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showExtract, setShowExtract] = useState(false);

  const run = <T,>(p: Promise<T>, after?: (r: T) => void) => {
    setBusy(true); setErr(null);
    p.then(r => after?.(r)).catch(e => setErr(e instanceof Error ? e.message : String(e))).finally(() => setBusy(false));
  };

  useEffect(() => {
    riskAnalysis.meta(studyId).then(setMeta).catch(e => setErr(String(e)));
    riskAnalysis.getInput(studyId).then(d => { setInput(d); setForm({ ...d.data }); }).catch(() => {});
    riskAnalysis.getCalc(studyId).then(setCalc).catch(() => {});
    riskAnalysis.matrix(studyId).then(setMatrix).catch(() => {});
  }, [studyId]);

  const groups = useMemo(() => {
    const m = new Map<string, AnalizaField[]>();
    meta?.fields.forEach(f => { if (!m.has(f.group)) m.set(f.group, []); m.get(f.group)!.push(f); });
    return Array.from(m.entries());
  }, [meta]);

  const setField = (key: string, value: unknown) => setForm(prev => ({ ...prev, [key]: value }));

  const saveInput = () => run(riskAnalysis.saveInput(studyId, form, input?.source ?? 'MANUAL'),
    d => { setInput(d); setForm({ ...d.data }); });

  const doCalc = () => run(riskAnalysis.calc(studyId), r => {
    setCalcRun(r);
    riskAnalysis.getCalc(studyId).then(setCalc).catch(() => {});
  });

  const reloadMatrix = () => riskAnalysis.matrix(studyId).then(setMatrix).catch(() => {});
  const seed = () => run(riskAnalysis.seedHazards(studyId), reloadMatrix);
  const aiSuggest = () => run(riskAnalysis.aiSuggest(studyId), reloadMatrix);
  const addHazard = () => run(riskAnalysis.addHazard(studyId, { title: 'Nowe zagrożenie', p: 1, c: 1, v: 1 }), reloadMatrix);
  const updateHazard = (hid: number, patch: Record<string, unknown>) => run(riskAnalysis.updateHazard(studyId, hid, patch), reloadMatrix);
  const deleteHazard = (hid: number) => run(riskAnalysis.deleteHazard(studyId, hid), reloadMatrix);

  if (!meta) return null;

  return (
    <div className="card mb-4">
      <div className="card-head">
        <div className="card-title">Analiza ryzyka (art. 133) — dane, obliczenia, macierz P×C×V</div>
        <div className="ml-auto flex gap-1">
          {(['dane', 'obliczenia', 'macierz'] as Tab[]).map(t => (
            <button key={t} onClick={() => setTab(t)}
                    className={'btn btn-sm ' + (tab === t ? 'btn-primary' : 'btn-ghost')}>
              {t === 'dane' ? 'Dane' : t === 'obliczenia' ? 'Obliczenia' : 'Macierz ryzyka'}
            </button>
          ))}
        </div>
      </div>
      <div className="card-body space-y-4">
        {err && <div className="text-red-500 text-[13px]">Błąd: {err}</div>}

        {tab === 'dane' && (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              {input && (
                <span className={'pill ' + (input.complete ? 'pill-green' : 'pill-amber')}>
                  {input.complete ? 'Dane kompletne' : `Braki: ${input.missingFields.length}`}
                </span>
              )}
              {!readOnly && (
                <button className="btn btn-secondary btn-sm" onClick={() => setShowExtract(true)}>
                  <Icon name="sparkles" className="w-3.5 h-3.5"/> Wypełnij z arkusza (AI)
                </button>
              )}
              {!readOnly && (
                <button className="btn btn-primary btn-sm ml-auto" onClick={saveInput} disabled={busy}>
                  {busy ? 'Zapisuję…' : 'Zapisz dane'}
                </button>
              )}
            </div>
            {input && !input.complete && input.missingFields.length > 0 && (
              <div className="text-[12px] text-ink-500">Brakujące pola wymagane: {input.missingFields.join(', ')}.</div>
            )}
            {groups.map(([group, fields]) => (
              <fieldset key={group} className="border border-ink-100 rounded p-3">
                <legend className="text-[12px] font-semibold text-ink-600 px-1">{group}</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {fields.map(f => (
                    <FieldInput key={f.key} field={f} value={form[f.key]} meta={meta} readOnly={readOnly}
                                onChange={v => setField(f.key, v)}/>
                  ))}
                </div>
              </fieldset>
            ))}
          </>
        )}

        {tab === 'obliczenia' && (
          <>
            {!readOnly && (
              <button className="btn btn-primary btn-sm" onClick={doCalc} disabled={busy}>
                {busy ? 'Liczę…' : 'Przelicz (Wyssling, obszar R, tₐ)'}
              </button>
            )}
            {calcRun && (
              <div className="space-y-1">
                {calcRun.outcomes.map(o => (
                  <div key={o.method} className="text-[12px] flex items-start gap-2">
                    <span className={'pill ' + (o.ok ? 'pill-green' : 'pill-red')}>{o.ok ? 'OK' : 'brak'}</span>
                    <span className="font-mono text-ink-500">{o.method}</span>
                    <span className="text-ink-700">{o.ok ? o.summary : o.error}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="table-hw text-[12px]">
                <thead><tr><th>Metoda</th><th>Wynik</th></tr></thead>
                <tbody>
                  {calc.map(c => (
                    <tr key={c.id}><td className="font-mono">{c.method}</td><td>{c.summary}</td></tr>
                  ))}
                  {calc.length === 0 && <tr><td colSpan={2} className="text-ink-400 py-3 text-center">Brak obliczeń — uruchom „Przelicz".</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="text-[11px] text-ink-400">
              tₐ &lt; 25 lat → przesłanka do ustanowienia strefy ochrony pośredniej (art. 133 ust. 5 pkt 1).
            </div>
          </>
        )}

        {tab === 'macierz' && matrix && (
          <>
            {!readOnly && (
              <div className="flex gap-2 flex-wrap">
                <button className="btn btn-secondary btn-sm" onClick={seed} disabled={busy}>Zasiej bibliotekę art. 130</button>
                <button className="btn btn-ai btn-sm" onClick={aiSuggest} disabled={busy}>
                  <Icon name="sparkles" className="w-3.5 h-3.5"/> AI: zaproponuj zagrożenia
                </button>
              </div>
            )}
            <PcvMatrix matrix={matrix} riskClasses={meta.riskClasses} readOnly={readOnly}
                       onUpdate={updateHazard} onDelete={deleteHazard}/>
            {!readOnly && (
              <button className="btn btn-ghost btn-sm" onClick={addHazard} disabled={busy}>
                <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj
              </button>
            )}
          </>
        )}
      </div>

      <div className="card-body pt-0">
        <a className="btn btn-ghost btn-sm" href={pdfApi.podgladUrl(studyId)} target="_blank" rel="noreferrer">
          <Icon name="file-text" className="w-3.5 h-3.5"/> Podgląd operatu (PDF)
        </a>
      </div>

      {showExtract && (
        <AnalizaExtractModal studyId={studyId} meta={meta} onClose={() => setShowExtract(false)}
          onConfirm={fields => {
            setForm(prev => ({ ...prev, ...fields }));
            setShowExtract(false);
            run(riskAnalysis.saveInput(studyId, { ...form, ...fields }, 'AI_XLSX'),
              d => { setInput(d); setForm({ ...d.data }); });
          }}/>
      )}
    </div>
  );
}

function FieldInput({ field, value, meta, readOnly, onChange }: {
  field: AnalizaField; value: unknown; meta: AnalizaMeta; readOnly?: boolean;
  onChange: (v: unknown) => void;
}) {
  if (field.key === 'ta_layers') {
    return <LayersEditor value={value} readOnly={readOnly} onChange={onChange}/>;
  }
  const label = field.unit ? `${field.label} [${field.unit}]` : field.label;
  const req = field.required ? <span className="text-red-500"> *</span> : null;
  if (field.kind === 'SELECT' && field.dictType) {
    const opts = meta.dictionaries[field.dictType] ?? [];
    return (
      <label className="field">
        <span className="field-label">{label}{req}</span>
        <SearchableSelect value={String(value ?? '')} disabled={readOnly} onChange={v => onChange(v)}>
          <option value="">—</option>
          {opts.map(o => <option key={o.label} value={o.label}>{o.label}</option>)}
        </SearchableSelect>
      </label>
    );
  }
  if (field.kind === 'TEXTAREA') {
    return (
      <label className="field sm:col-span-2">
        <span className="field-label">{label}{req}</span>
        <textarea className="textarea" rows={2} value={String(value ?? '')} disabled={readOnly}
                  onChange={e => onChange(e.target.value)}/>
      </label>
    );
  }
  return (
    <label className="field">
      <span className="field-label">{label}{req}</span>
      <input className="input" type={field.kind === 'NUMBER' ? 'number' : 'text'} step="any"
             value={String(value ?? '')} disabled={readOnly}
             onChange={e => onChange(field.kind === 'NUMBER'
               ? (e.target.value === '' ? '' : Number(e.target.value))
               : e.target.value)}/>
    </label>
  );
}

/** Edytor warstw nadkładu do tₐ: lista {m, ne, k[m/d]}. */
function LayersEditor({ value, readOnly, onChange }: {
  value: unknown; readOnly?: boolean; onChange: (v: unknown) => void;
}) {
  const layers: Layer[] = Array.isArray(value) ? (value as Layer[]) : [];
  const set = (i: number, key: keyof Layer, v: string) => {
    const next = layers.map((l, idx) => idx === i ? { ...l, [key]: v === '' ? '' : Number(v) } : l);
    onChange(next);
  };
  const add = () => onChange([...layers, { m: '', ne: '', k: '' }]);
  const del = (i: number) => onChange(layers.filter((_, idx) => idx !== i));
  return (
    <div className="sm:col-span-2 field">
      <span className="field-label">Warstwy nadkładu izolującego (tₐ) <span className="text-red-500">*</span></span>
      <div className="space-y-1">
        {layers.map((l, i) => (
          <div key={i} className="flex gap-1 items-center">
            <input className="input btn-sm w-24" type="number" step="any" placeholder="m [m]" disabled={readOnly}
                   value={String(l.m ?? '')} onChange={e => set(i, 'm', e.target.value)}/>
            <input className="input btn-sm w-24" type="number" step="any" placeholder="nₑ [-]" disabled={readOnly}
                   value={String(l.ne ?? '')} onChange={e => set(i, 'ne', e.target.value)}/>
            <input className="input btn-sm w-28" type="number" step="any" placeholder="k [m/d]" disabled={readOnly}
                   value={String(l.k ?? '')} onChange={e => set(i, 'k', e.target.value)}/>
            {!readOnly && <button className="text-ink-400 hover:text-red-500" onClick={() => del(i)}><Icon name="x" className="w-3.5 h-3.5"/></button>}
          </div>
        ))}
        {!readOnly && <button className="btn btn-ghost btn-sm" onClick={add}><Icon name="plus" className="w-3 h-3"/> Dodaj warstwę</button>}
      </div>
    </div>
  );
}
